import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Train, LogOut, Plus, Trash2, Edit, Camera, MapPin, User, Inbox, ShieldAlert, CheckCircle2, XCircle, Users as UsersIcon, KeyRound, ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { BrandChip } from "@/components/BrandChip";
import { DepotSeal } from "@/components/DepotSeal";
import { AdminEditDepotDialog } from "@/components/AdminEditDepotDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [stats, setStats] = useState(null);
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [tab, setTab] = useState("overview");
  const [submissions, setSubmissions] = useState([]);
  const [takedowns, setTakedowns] = useState([]);
  const [users, setUsers] = useState([]);
  const [modLoading, setModLoading] = useState(false);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "editor" });
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    // If we have user from navigation state, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }

    // Check authentication
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        // Not authenticated, redirect to login
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await axios.get(`${API}/depots?search=${searchQuery}&limit=20`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/admin/photos/${photoId}`, { withCredentials: true });
      toast.success("Photo deleted");
      setRecentPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete photo");
    }
  };

  const loadSubmissions = async () => {
    setModLoading(true);
    try {
      const r = await axios.get(`${API}/admin/submissions`, { withCredentials: true });
      setSubmissions(r.data);
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setModLoading(false);
    }
  };

  const loadTakedowns = async () => {
    setModLoading(true);
    try {
      const r = await axios.get(`${API}/admin/takedown-requests`, { withCredentials: true });
      setTakedowns(r.data);
    } catch (e) {
      toast.error("Failed to load takedowns");
    } finally {
      setModLoading(false);
    }
  };

  const approveSubmission = async (id) => {
    try {
      await axios.post(`${API}/admin/submissions/${id}/approve`, {}, { withCredentials: true });
      toast.success("Approved and applied");
      loadSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  const rejectSubmission = async (id) => {
    const reason = window.prompt("Reason for rejecting (optional):", "");
    try {
      await axios.post(
        `${API}/admin/submissions/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`,
        {},
        { withCredentials: true }
      );
      toast.success("Rejected");
      loadSubmissions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  const resolveTakedown = async (id, status) => {
    try {
      await axios.post(
        `${API}/admin/takedown-requests/${id}/resolve?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Marked ${status}`);
      loadTakedowns();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const loadUsers = async () => {
    setModLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(r.data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setModLoading(false);
    }
  };

  const promoteUser = async (uid) => {
    try {
      await axios.post(`${API}/admin/users/${uid}/promote`, {}, { withCredentials: true });
      toast.success("Promoted to admin");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const demoteUser = async (uid) => {
    if (!window.confirm("Remove admin rights from this user?")) return;
    try {
      await axios.post(`${API}/admin/users/${uid}/demote`, {}, { withCredentials: true });
      toast.success("Admin rights removed");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setUserStatus = async (uid, action) => {
    try {
      await axios.post(`${API}/admin/users/${uid}/${action}`, {}, { withCredentials: true });
      toast.success(action === "approve" ? "Account approved" : "Account rejected");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setUserMod = async (uid, makeMod) => {
    try {
      await axios.post(
        `${API}/admin/users/${uid}/${makeMod ? "promote-mod" : "demote-mod"}`,
        {},
        { withCredentials: true }
      );
      toast.success(makeMod ? "Now a moderator" : "Moderator rights removed");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const resetUserPassword = async (uid, email) => {
    if (!window.confirm(`Generate a one-time password for ${email}? Their existing sessions will be terminated.`)) return;
    try {
      const r = await axios.post(`${API}/admin/users/${uid}/reset-password`, {}, { withCredentials: true });
      window.prompt(
        `Copy this one-time password for ${email} and share it privately:`,
        r.data.temporary_password
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Permanently delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${uid}`, { withCredentials: true });
      toast.success("User deleted");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const createBackendUser = async () => {
    if (!newUser.email.trim() || !newUser.name.trim() || newUser.password.length < 8) {
      toast.error("Email, name and a password (min 8 chars) are required");
      return;
    }
    try {
      setCreatingUser(true);
      await axios.post(
        `${API}/admin/users`,
        { ...newUser, email: newUser.email.trim().toLowerCase(), name: newUser.name.trim() },
        { withCredentials: true }
      );
      toast.success(`Created ${newUser.role}: ${newUser.email}`);
      setNewUserOpen(false);
      setNewUser({ email: "", name: "", password: "", role: "editor" });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === "submissions") loadSubmissions();
    if (tab === "takedowns") loadTakedowns();
    if (tab === "users") loadUsers();
  }, [tab, user]);

  // No third-party OAuth: just redirect to /login
  const handleLogin = () => navigate("/login");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Card className="w-full max-w-md bg-[#111] border-white/5">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 text-amber-500">
              <DepotSeal size={68} />
            </div>
            <CardTitle className="text-2xl text-white font-serif">Admin Sign In</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-white/50 text-center mb-6">
              Please sign in to access the admin dashboard.
            </p>
            <Button
              onClick={handleLogin}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid="admin-login-btn"
            >
              Go to sign in
            </Button>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-white/50 hover:text-amber-500">
                ← Back to site
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
              <BrandChip size={40} to={null} />
              <span className="font-serif text-lg font-bold text-white">Admin Dashboard</span>
            </Link>
            
            <div className="flex items-center gap-4">
              {(user.is_admin || user.is_mod) && (
                <AdminEditDepotDialog
                  mode="create"
                  onChange={(created) => {
                    if (created?.id) navigate(`/depot/${created.id}`);
                  }}
                  trigger={
                    <Button
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                      data-testid="admin-header-add-depot"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Depot
                    </Button>
                  }
                />
              )}
              <Link
                to="/"
                className="hidden md:flex items-center gap-1.5 text-[13px] uppercase tracking-[0.15em] text-white/80 hover:text-amber-500 transition-colors"
                data-testid="admin-back-to-map"
              >
                <MapPin className="w-4 h-4" />
                View Map
              </Link>
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                )}
                <span className="text-white/70 text-sm hidden sm:block">{user.name}</span>
                {user.is_admin ? (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded-full">Admin</span>
                ) : user.is_mod ? (
                  <span className="px-2 py-0.5 bg-sky-500/20 text-sky-400 text-xs rounded-full">Moderator</span>
                ) : null}
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white">
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {!user.is_admin && !user.is_mod ? (
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-8 text-center">
              <User className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
              <p className="text-white/50 mb-4">
                Your account ({user.email}) is not authorized for admin access.
              </p>
              <p className="text-white/30 text-sm">
                {user.status === "pending"
                  ? "Your account is pending administrator approval."
                  : "Contact the site administrator to request access."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Tab bar */}
            <div className="flex gap-2 mb-6 border-b border-white/5 pb-3 overflow-x-auto" data-testid="admin-tabs">
              {[
                ["overview", "Overview", MapPin],
                ["submissions", "Submissions", Inbox],
                ["takedowns", "Takedowns", ShieldAlert],
                ...(user.is_admin ? [["users", "Users", UsersIcon]] : []),
              ].map(([id, label, Icon]) => (
                <Button
                  key={id}
                  onClick={() => setTab(id)}
                  variant={tab === id ? "default" : "ghost"}
                  className={
                    tab === id
                      ? "bg-amber-500 hover:bg-amber-600 text-black"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }
                  data-testid={`tab-${id}`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {label}
                </Button>
              ))}
            </div>

            {tab === "overview" && (
              <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-[#111] border-white/5">
                  <CardContent className="p-5 text-center">
                    <p className="font-mono text-3xl font-bold stat-number">{stats.total_depots.toLocaleString()}</p>
                    <p className="text-sm text-white/40 mt-1">Total Depots</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#111] border-white/5">
                  <CardContent className="p-5 text-center">
                    <p className="font-mono text-3xl font-bold text-white">{stats.states_count}</p>
                    <p className="text-sm text-white/40 mt-1">States/Provinces</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#111] border-white/5">
                  <CardContent className="p-5 text-center">
                    <p className="font-mono text-3xl font-bold text-white">{stats.total_photos.toLocaleString()}</p>
                    <p className="text-sm text-white/40 mt-1">Photos</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#111] border-white/5">
                  <CardContent className="p-5 text-center">
                    <p className="font-mono text-3xl font-bold text-emerald-400">{stats.coverage_percent}%</p>
                    <p className="text-sm text-white/40 mt-1">Coverage</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Search Depots */}
            <Card className="bg-[#111] border-white/5 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-amber-500" />
                  Search & Manage Depots
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-4">
                  <Input
                    placeholder="Search by depot name or city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    className="bg-white/5 border-white/10 text-white"
                  />
                  <Button onClick={handleSearch} className="bg-amber-500 hover:bg-amber-600 text-black">
                    Search
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {searchResults.map((depot) => (
                      <div key={depot.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div>
                          <p className="font-medium text-white">{depot.name}</p>
                          <p className="text-sm text-white/50">{depot.city}, {depot.state}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/depot/${depot.id}`}>
                            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                              View
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="bg-[#111] border-white/5">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-white mb-4">Quick Actions</h3>
                  <div className="space-y-2">
                    <AdminEditDepotDialog
                      mode="create"
                      onChange={(created) => {
                        if (created?.id) navigate(`/depot/${created.id}`);
                      }}
                      trigger={
                        <Button
                          variant="outline"
                          className="w-full justify-start bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 hover:!text-emerald-200 hover:border-emerald-500/60"
                          data-testid="admin-quickaction-add-depot"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add New Depot
                        </Button>
                      }
                    />
                    <Link to="/" className="block">
                      <Button variant="outline" className="w-full justify-start bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30">
                        <MapPin className="w-4 h-4 mr-2" />
                        View Map
                      </Button>
                    </Link>
                    <Link to="/states" className="block">
                      <Button variant="outline" className="w-full justify-start bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30">
                        <Train className="w-4 h-4 mr-2" />
                        Browse States
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111] border-white/5">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-white mb-4">Account Info</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-white/70"><span className="text-white/40">Email:</span> {user.email}</p>
                    <p className="text-white/70"><span className="text-white/40">Name:</span> {user.name}</p>
                    <p className="text-white/70"><span className="text-white/40">Role:</span> {user.is_admin ? "Administrator" : "User"}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
              </>
            )}

            {tab === "submissions" && (
              <Card className="bg-[#111] border-white/5" data-testid="submissions-panel">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Inbox className="w-5 h-5 text-amber-500" />
                    User Submissions
                  </CardTitle>
                  <Button variant="outline" onClick={loadSubmissions} className="border-white/10 text-white hover:bg-white/5">
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {modLoading ? (
                    <p className="text-white/50 py-8 text-center">Loading…</p>
                  ) : submissions.length === 0 ? (
                    <p className="text-white/40 py-8 text-center">No submissions yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {submissions.map((s) => (
                        <div key={s.id} className="p-4 bg-white/5 rounded-lg border border-white/5" data-testid={`submission-row-${s.id}`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <p className="text-white font-medium truncate">
                                <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400 mr-2">
                                  {s.kind}
                                </span>
                                {s.payload?.name || s.target_id || "(no name)"}
                              </p>
                              <p className="text-xs text-white/50 mt-1">
                                by {s.submitter_email} · {new Date(s.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                s.status === "pending"
                                  ? "bg-yellow-500/20 text-yellow-400"
                                  : s.status === "approved"
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : "bg-red-500/20 text-red-400"
                              }`}
                            >
                              {s.status}
                            </span>
                          </div>
                          <details className="text-sm text-white/70">
                            <summary className="cursor-pointer text-white/50 hover:text-white">
                              View payload
                            </summary>
                            <pre className="mt-2 p-2 bg-black/40 rounded text-xs overflow-auto max-h-64">
                              {JSON.stringify(s.payload, null, 2)}
                            </pre>
                            {s.notes && (
                              <p className="mt-2 text-white/60">
                                <span className="text-white/40">Notes:</span> {s.notes}
                              </p>
                            )}
                            {s.target_id && (
                              <Link to={`/depot/${s.target_id}`} className="text-amber-400 text-xs hover:underline mt-2 inline-block">
                                View current record →
                              </Link>
                            )}
                          </details>
                          {s.photo_data && (
                            <div className="mt-3" data-testid={`submission-photo-${s.id}`}>
                              <p className="text-xs text-amber-400 mb-1 uppercase tracking-wider">
                                Attached photo{s.photo_caption ? ` — "${s.photo_caption}"` : ""}
                              </p>
                              <img
                                src={s.photo_data}
                                alt="submission attachment"
                                className="max-h-48 rounded border border-white/10 bg-black/40"
                              />
                            </div>
                          )}
                          {s.status === "pending" && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                onClick={() => approveSubmission(s.id)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`approve-btn-${s.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => rejectSubmission(s.id)}
                                variant="outline"
                                className="border-white/10 text-white hover:bg-white/5"
                                data-testid={`reject-btn-${s.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {tab === "takedowns" && (
              <Card className="bg-[#111] border-white/5" data-testid="takedowns-panel">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-500" />
                    Takedown Requests
                  </CardTitle>
                  <Button variant="outline" onClick={loadTakedowns} className="border-white/10 text-white hover:bg-white/5">
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {modLoading ? (
                    <p className="text-white/50 py-8 text-center">Loading…</p>
                  ) : takedowns.length === 0 ? (
                    <p className="text-white/40 py-8 text-center">No takedown requests.</p>
                  ) : (
                    <div className="space-y-3">
                      {takedowns.map((t) => (
                        <div key={t.id} className="p-4 bg-white/5 rounded-lg border border-white/5" data-testid={`takedown-row-${t.id}`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <p className="text-white font-medium">
                                {t.requester_name || "Anonymous"} — {t.contact_email}
                              </p>
                              <p className="text-xs text-white/50 mt-1">
                                {new Date(t.created_at).toLocaleString()}
                                {t.relationship_to_work && ` · ${t.relationship_to_work}`}
                              </p>
                            </div>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                t.status === "pending"
                                  ? "bg-yellow-500/20 text-yellow-400"
                                  : t.status === "resolved"
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : "bg-red-500/20 text-red-400"
                              }`}
                            >
                              {t.status}
                            </span>
                          </div>
                          <p className="text-sm text-white/70 whitespace-pre-wrap">{t.reason}</p>
                          {(t.photo_id || t.depot_or_tower_id) && (
                            <p className="text-xs text-white/50 mt-2 font-mono">
                              {t.photo_id && `photo: ${t.photo_id}`}
                              {t.depot_or_tower_id && ` / target: ${t.depot_or_tower_id}`}
                            </p>
                          )}
                          {t.status === "pending" && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                onClick={() => resolveTakedown(t.id, "resolved")}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`resolve-btn-${t.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Mark resolved
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => resolveTakedown(t.id, "rejected")}
                                variant="outline"
                                className="border-white/10 text-white hover:bg-white/5"
                                data-testid={`dismiss-btn-${t.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {tab === "users" && (
              <Card className="bg-[#111] border-white/5" data-testid="users-panel">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <UsersIcon className="w-5 h-5 text-amber-500" />
                    Backend Users
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setNewUserOpen(true)}
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                      data-testid="new-user-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New backend user
                    </Button>
                    <Button variant="outline" onClick={loadUsers} className="border-white/10 text-white hover:bg-white/5">
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {modLoading ? (
                    <p className="text-white/50 py-8 text-center">Loading…</p>
                  ) : users.length === 0 ? (
                    <p className="text-white/40 py-8 text-center">No users yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {users.map((u) => (
                        <div
                          key={u.user_id}
                          className="flex flex-wrap items-center justify-between gap-3 p-3 bg-white/5 rounded-lg border border-white/5"
                          data-testid={`user-row-${u.user_id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">
                              {u.name || "(no name)"}{" "}
                              {u.is_admin && (
                                <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">
                                  admin
                                </span>
                              )}
                              {u.is_mod && !u.is_admin && (
                                <span className="ml-2 px-2 py-0.5 bg-sky-500/20 text-sky-400 text-xs rounded">
                                  mod
                                </span>
                              )}
                              {u.status === "pending" && (
                                <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                                  pending approval
                                </span>
                              )}
                              {u.status === "rejected" && (
                                <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                  rejected
                                </span>
                              )}
                              {u.user_id === user.user_id && (
                                <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">
                                  you
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-white/50 mt-0.5">
                              {u.email}
                              {u.created_at && (
                                <span className="ml-2 text-white/30">
                                  · joined {new Date(u.created_at).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {u.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => setUserStatus(u.user_id, "approve")}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid={`approve-user-btn-${u.user_id}`}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => setUserStatus(u.user_id, "reject")}
                                  variant="outline"
                                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                                  data-testid={`reject-user-btn-${u.user_id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {u.status === "approved" && !u.is_admin && (
                              !u.is_mod ? (
                                <Button
                                  size="sm"
                                  onClick={() => setUserMod(u.user_id, true)}
                                  variant="outline"
                                  className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10"
                                  data-testid={`mod-btn-${u.user_id}`}
                                >
                                  Make mod
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => setUserMod(u.user_id, false)}
                                  variant="outline"
                                  className="border-white/10 text-white hover:bg-white/5"
                                  data-testid={`unmod-btn-${u.user_id}`}
                                >
                                  Remove mod
                                </Button>
                              )
                            )}
                            {u.status === "approved" && !u.is_admin ? (
                              <Button
                                size="sm"
                                onClick={() => promoteUser(u.user_id)}
                                className="bg-amber-500 hover:bg-amber-600 text-black"
                                data-testid={`promote-btn-${u.user_id}`}
                              >
                                <ShieldCheck className="w-4 h-4 mr-1" />
                                Make admin
                              </Button>
                            ) : u.is_admin && u.user_id !== user.user_id ? (
                              <Button
                                size="sm"
                                onClick={() => demoteUser(u.user_id)}
                                variant="outline"
                                className="border-white/10 text-white hover:bg-white/5"
                                data-testid={`demote-btn-${u.user_id}`}
                              >
                                <ShieldOff className="w-4 h-4 mr-1" />
                                Remove admin
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              onClick={() => resetUserPassword(u.user_id, u.email)}
                              variant="outline"
                              className="border-white/10 text-white hover:bg-white/5"
                              data-testid={`reset-btn-${u.user_id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-1" />
                              Reset password
                            </Button>
                            {u.user_id !== user.user_id && (
                              <Button
                                size="sm"
                                onClick={() => deleteUser(u.user_id, u.email)}
                                variant="outline"
                                className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                                data-testid={`delete-btn-${u.user_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* New backend user modal */}
      <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">New backend user</DialogTitle>
            <p className="text-sm text-white/60 pt-1">
              Create an editor or admin account directly. Share the password securely.
            </p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm text-white/80 mb-1 block" htmlFor="nu-email">Email</label>
              <Input
                id="nu-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <label className="text-sm text-white/80 mb-1 block" htmlFor="nu-name">Name</label>
              <Input
                id="nu-name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <label className="text-sm text-white/80 mb-1 block" htmlFor="nu-pw">
                Password <span className="text-white/40 text-xs ml-1">(min. 8 chars)</span>
              </label>
              <Input
                id="nu-pw"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="bg-white/5 border-white/10 text-white font-mono"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <label className="text-sm text-white/80 mb-1 block">Role</label>
              <div className="flex gap-2">
                {[
                  ["editor", "Editor — moderates submissions & takedowns"],
                  ["admin", "Admin — full site control"],
                ].map(([r, label]) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setNewUser({ ...newUser, role: r })}
                    className={`flex-1 text-left p-3 rounded-lg border transition-colors ${
                      newUser.role === r
                        ? "border-amber-500 bg-amber-500/10"
                        : "border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`new-user-role-${r}`}
                  >
                    <div className="font-medium capitalize text-white">{r}</div>
                    <div className="text-xs text-white/60 mt-0.5">{label.split(" — ")[1]}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewUserOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={createBackendUser}
              disabled={creatingUser}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="new-user-create-btn"
            >
              {creatingUser ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
